import { useState } from "react";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, useMapEvents } from "react-leaflet";
import { toast } from "sonner";
import { X, Undo2, Eraser, Save, Loader2, MousePointerClick } from "lucide-react";
import api from "@/lib/api";

function Clicker({ onAdd }) {
  useMapEvents({ click(e) { onAdd([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

export default function BoundaryEditor({ parcel, onClose, onSaved }) {
  const [pts, setPts] = useState(
    Array.isArray(parcel.boundary) && parcel.boundary.length ? parcel.boundary : []
  );
  const [loading, setLoading] = useState(false);

  const center =
    parcel.latitude != null && parcel.longitude != null
      ? [parcel.latitude, parcel.longitude]
      : pts[0] || [7.54, -5.55];
  const color = "#c65a2e";

  const save = async () => {
    if (pts.length < 3) { toast.error("Tracez au moins 3 points pour former une zone"); return; }
    setLoading(true);
    try {
      const { data } = await api.patch(`/parcels/${parcel.id}/boundary`, { boundary: pts });
      toast.success(`Limites enregistrées — superficie calculée : ${data.superficie} ha`);
      onSaved();
    } catch { toast.error("Échec de l'enregistrement des limites"); }
    finally { setLoading(false); }
  };

  return (
    <div className="dark fixed inset-0 z-[1100] grid place-items-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-4xl bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="font-heading text-2xl text-foreground">Tracer les limites — {parcel.reference}</h2>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
              <MousePointerClick className="w-3.5 h-3.5" /> Cliquez sur la carte pour ajouter les sommets de la parcelle.
            </p>
          </div>
          <button data-testid="boundary-close-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="relative isolate" data-testid="boundary-map">
          <MapContainer center={center} zoom={pts.length ? 14 : 13} style={{ height: 460, width: "100%" }} scrollWheelZoom>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Clicker onAdd={(p) => setPts((prev) => [...prev, p])} />
            {pts.length >= 3 && <Polygon positions={pts} pathOptions={{ color, fillColor: color, fillOpacity: 0.25, weight: 2 }} />}
            {pts.length === 2 && <Polyline positions={pts} pathOptions={{ color, weight: 2 }} />}
            {pts.map((p, i) => (
              <CircleMarker key={i} center={p} radius={5} pathOptions={{ color: "#fff", fillColor: color, fillOpacity: 1, weight: 2 }} />
            ))}
          </MapContainer>

          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-background/90 backdrop-blur border border-border rounded-full px-4 py-1.5 text-xs text-muted-foreground">
            {pts.length} sommet{pts.length > 1 ? "s" : ""} placé{pts.length > 1 ? "s" : ""}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border">
          <div className="flex gap-2">
            <button data-testid="boundary-undo-btn" onClick={() => setPts((p) => p.slice(0, -1))} disabled={!pts.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:border-secondary transition-colors disabled:opacity-40">
              <Undo2 className="w-4 h-4" /> Annuler point
            </button>
            <button data-testid="boundary-clear-btn" onClick={() => setPts([])} disabled={!pts.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-40">
              <Eraser className="w-4 h-4" /> Tout effacer
            </button>
          </div>
          <button data-testid="boundary-save-btn" onClick={save} disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-secondary text-secondary-foreground font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer les limites
          </button>
        </div>
      </div>
    </div>
  );
}
